import type { SessionState } from '../domain/session.ts';
import { createSession } from '../domain/session.ts';
import { SessionLoadRefusal, inspectSessionRecord } from './recordCompatibility.ts';
import type { StoragePort } from './storagePort.ts';
import { WriteQueue } from './writeQueue.ts';

/**
 * Saves and restores the unfinished session for a character.
 *
 * One session file per character. Opening a crawler resumes its session if one
 * was left unfinished; ending the session deletes the file.
 */
export class SessionRepository {
  private readonly storage: StoragePort;
  private readonly now: () => string;
  private readonly queue: WriteQueue;

  constructor(
    storage: StoragePort,
    now: () => string = () => new Date().toISOString(),
    queue: WriteQueue = new WriteQueue(),
  ) {
    this.storage = storage;
    this.now = now;
    this.queue = queue;
  }

  /**
   * The saved session, or a fresh one when the crawler has none.
   *
   * "OrStart" APPLIES TO ABSENCE ONLY, the same rule A.1 established for the
   * runtime and for the same reason. This used to start a fresh session
   * whenever the stored one failed to validate, which quietly erased a
   * crawler's roll history, their `untrainedSkillsAttempted` and any pending
   * untrained award - and the fresh record then looked authoritative enough for
   * the next save to write over the top of the real one.
   *
   * An existing record this build cannot understand raises a typed
   * `SessionLoadRefusal` and is left exactly where it is.
   */
  async loadOrStart(characterId: string): Promise<SessionState> {
    const raw = await this.storage.readSession(characterId);
    if (raw === null) return createSession(characterId, this.now());

    const inspected = inspectSessionRecord(raw, characterId);
    if (!inspected.ok) throw new SessionLoadRefusal(inspected.kind, inspected.message);
    return inspected.record;
  }

  /** The record exactly as `save` would write it, without writing it. */
  stamp(session: SessionState): SessionState {
    return { ...session, updatedAt: this.now() };
  }

  async save(session: SessionState): Promise<SessionState> {
    const stamped = this.stamp(session);
    return this.queue.run(`session:${session.characterId}`, async () => {
      await this.storage.writeSession(stamped.characterId, stamped);
      return stamped;
    });
  }

  async end(characterId: string): Promise<void> {
    await this.queue.run(`session:${characterId}`, () => this.storage.deleteSession(characterId));
  }

  drain(): Promise<void> {
    return this.queue.drain();
  }
}
